import { Box, Flex, IconButton, ScrollArea, Text, TextArea } from '@radix-ui/themes';
import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { DeleteOutlineIcon, EditIcon, SaveIcon } from 'shared/icons';
import { DateUtilsService } from 'shared/utils/date-utils.service';
import { toast } from 'sonner';
import { useJobApplicants } from '../job-applicants-provider';
import { Candidate } from '../types';

function EmployeeCommentView({
   candidate,
   onClose,
}: {
   candidate: Candidate;
   onClose?: () => void;
}) {
   const { slug } = useParams();
   const jobApplicants = useJobApplicants();

   const [edit, setEdit] = useState<boolean>(false);

   const [comment, setComment] = useState(() => {
      return candidate.comment ?? '';
   });

   const textAreaRef = useRef<null | HTMLTextAreaElement>(null);

   const isAddLoading =
      jobApplicants.addEmployerCommentState.status === 'pending' &&
      jobApplicants.seletedCandidateId === candidate.user_id;

   const isRemoveLoading =
      jobApplicants.removeEmployerCommentState.status === 'pending' &&
      jobApplicants.seletedCandidateId === candidate.user_id;

   return (
      <Flex gap="3" width="100%">
         <Box flexGrow="1">
            <Flex gap="2" mb="2" mr="1" justify="between">
               <Flex ml="1">
                  <Text color="gray" size="2">
                     Коментар:
                  </Text>
                  {candidate.comment_date && (
                     <Text color="gray" size="2">
                        {DateUtilsService.toAppFormat(candidate.comment_date)}
                     </Text>
                  )}
               </Flex>

               <Flex gap="2">
                  {!edit && (
                     <>
                        <IconButton
                           size="1"
                           variant="ghost"
                           onClick={() => {
                              setEdit(true);
                              setTimeout(() => {
                                 textAreaRef.current?.focus();
                              }, 0);
                           }}
                        >
                           <EditIcon width={20} height={20} />
                        </IconButton>

                        <IconButton
                           size="1"
                           variant="ghost"
                           loading={isRemoveLoading}
                           disabled={!comment}
                           onClick={() => {
                              jobApplicants.removeEmployerComment(
                                 { id: candidate.job_apply_id, userId: candidate.user_id },
                                 {
                                    onSuccess(res) {
                                       if (slug) {
                                          jobApplicants.loadAccDetail({ slug: slug });
                                          setComment('');
                                          toast.warning('Коментар видалено');
                                       }
                                    },
                                    onError: () => {},
                                 }
                              );
                           }}
                        >
                           <DeleteOutlineIcon width={20} height={20} />
                        </IconButton>
                     </>
                  )}
                  {edit && (
                     <IconButton
                        size="1"
                        variant="ghost"
                        disabled={!comment}
                        loading={isAddLoading}
                        onClick={() => {
                           jobApplicants.addEmployerComment(
                              { id: candidate.job_apply_id, comment, userId: candidate.user_id },
                              {
                                 onSuccess(res) {
                                    setEdit(false);
                                    if (slug) {
                                       jobApplicants.loadAccDetail({ slug: slug });
                                       setComment(comment);
                                       toast.success('Коментар збережено');
                                    }
                                 },
                                 onError: (e: string) => {
                                    toast.error(e);
                                 },
                              }
                           );
                        }}
                     >
                        <SaveIcon width={20} height={20} />
                     </IconButton>
                  )}
               </Flex>
            </Flex>

            {edit ? (
               <TextArea
                  placeholder="Додати коментар"
                  ref={textAreaRef}
                  size="2"
                  value={comment}
                  onChange={(e) => {
                     setComment(e.target.value);
                  }}
                  style={{ height: 80 }}
               />
            ) : (
               <ScrollArea
                  style={{
                     maxHeight: comment ? 80 : 'min-content',
                     height: comment ? 'min-content' : 32,
                     maxWidth: '887.867px',
                     background: '#fffde5',
                     borderRadius: 4,
                  }}
               >
                  <Flex direction="column">
                     <Box
                        pr="5"
                        pl="2"
                        py="1"
                        style={{
                           background: 'inherit',
                           height: 'min-content',
                           borderRadius: '4px',
                        }}
                     >
                        <Text size="2">{comment}</Text>
                     </Box>
                  </Flex>
               </ScrollArea>
            )}
         </Box>
      </Flex>
   );
}

export const AddCommentOnCandidate = observer(EmployeeCommentView);
